import useAddComment from "@babackapp/private-api-client/client/use-add-comment";
import useGetCommentAttachmentPresignedUrl from "@babackapp/private-api-client/client/use-get-comment-attachment-presigned-url";
import { useUpdateReturnRequestInCache } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Loader, SendIcon, ImageIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { useState, useRef, useCallback } from "react";
import z from "zod";
import MentionTextarea from "./mention-textarea";
import { convertHeicFileToJpeg } from "@/utils/heic-image";
import { useShopPeople } from "@/lib/people/use-shop-people";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";

const formSchema = z.object({
  comment: z.string().max(255).optional(),
});

type ActivityInputProps = {
  returnRequestId: string;
  shopId: string;
};

type AttachmentData = {
  s3Key: string;
  previewUrl: string;
};

export default function ActivityInput({
  returnRequestId,
  shopId,
}: ActivityInputProps) {
  const t = useTranslation();
  const updateReturnRequestInCache = useUpdateReturnRequestInCache();
  const [attachments, setAttachments] = useState<AttachmentData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: getPresignedUrl } =
    useGetCommentAttachmentPresignedUrl();

  // Mentionable: members, and the Baback support for super admins only, so
  // merchants are not offered the Baback team in every shop (BAB-3035).
  const isSuperAdmin = useViewerIsSuperAdmin();
  // Only people who can receive the mention: active, and with an account.
  const members = useShopPeople().filter(
    (person) =>
      person.isActive &&
      Boolean(person.user.email) &&
      (!person.isBabackSupport || isSuperAdmin),
  );

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      comment: "",
    },
  });

  const commentValue = form.watch("comment") || "";
  const { mutate, isPending } = useAddComment({
    onSuccess(returnRequest) {
      form.reset();
      setAttachments([]);
      setMentionedUserIds([]);
      updateReturnRequestInCache({
        returnRequest,
      });
    },
  });

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const newAttachments: AttachmentData[] = [];

      for (const file of Array.from(files)) {
        const processedFile = await convertHeicFileToJpeg(file);
        const presignedResponse = await getPresignedUrl({
          body: {
            filename: processedFile.name,
            mimeType: processedFile.type,
          },
          params: {
            path: { shopId, returnRequestId },
          },
        });

        if (!presignedResponse) {
          throw new Error("Failed to get presigned URL");
        }

        const { url, fields, s3Key, fullUrl } = presignedResponse;

        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append("file", processedFile);

        const uploadResponse = await fetch(url, {
          method: "POST",
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error("Upload failed");
        }

        newAttachments.push({ s3Key, previewUrl: fullUrl });
      }

      setAttachments([...attachments, ...newAttachments]);
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const onSubmit = form.handleSubmit((data) => {
    mutate({
      body: {
        ...(data.comment && { comment: data.comment }),
        ...(attachments.length > 0 && {
          attachments: attachments.map((a) => a.s3Key),
        }),
        ...(mentionedUserIds.length > 0 && { mentionedUserIds }),
      },
      params: {
        path: {
          returnRequestId,
          shopId,
        },
      },
    });
  });

  const handleSumbitOnCtrlEnterKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      onSubmit();
    }
  };

  const handleMentionsChange = useCallback((userIds: string[]) => {
    setMentionedUserIds(userIds);
  }, []);

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="relative border border-border rounded-md bg-background"
      >
        <FormField
          render={({ field }) => {
            return (
              <MentionTextarea
                className="bg-transparent resize-none pt-3 px-4 border-none focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder={t("returnDetail.comment.placeholder")}
                onChange={field.onChange}
                value={field.value}
                onKeyDown={handleSumbitOnCtrlEnterKeyDown}
                members={members ?? []}
                onMentionsChange={handleMentionsChange}
              />
            );
          }}
          name={"comment"}
        />
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 pb-2">
            {attachments.map((attachment, index) => (
              <div key={index} className="relative">
                <img
                  src={attachment.previewUrl}
                  alt={`Attachment ${index + 1}`}
                  className="h-16 w-16 object-cover rounded border"
                />
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 px-4 pb-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={isUploading || isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <ImageIcon className="h-4 w-4" />
            )}
          </Button>
          <Button
            size="sm"
            disabled={
              isPending ||
              (commentValue.trim() === "" && attachments.length === 0)
            }
          >
            {isPending ? (
              <div className="absolute inset-0 rounded-full z-30 pointer-events-none flex items-center justify-center">
                <Loader className="w-4 h-4" />
              </div>
            ) : (
              <>
                Send
                <SendIcon className={"h-4 w-4"} />
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
